import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { DataService } from "../../../_services/data.service";
import { AuthenticationService } from "../../../_services/authentication.service";
import { EventsService } from "../../../_services/eventsServices/events.service";
import { NgbModal, NgbModalRef, NgbTabset } from "@ng-bootstrap/ng-bootstrap";
import { ActivatedRoute, Router } from "@angular/router";

import { devToastMessage } from "../../../_helpers/devToastMessage";

import { SessionManagerService } from "../../../_services/SessionManager/session-manager.service";
import { GenericModalComponent } from "../../../form/generic-app-modal/generic-modal.component";
import { EventValidationService } from "../../../_services/eventsServices/eventValidation.service";
import { TranslationService } from "../../../_services/translation.service";
import { Observable } from "rxjs/Observable";
import { ITranslations } from "../../../_models/ITranslations";
import { UserManagementService } from "../../../_services/user-management.service";
import { UserManagementFormPersonalDetailsComponent } from "../../users/user-management-form-personal-details/user-management-form-personal-details.component";
import { BehaviorSubject } from "rxjs/BehaviorSubject";

import { DiscountManagementService } from "../../../_services/discount-management.service";
import { EditDiscountFormState } from "../discount-models/EditDiscountFormState";
import { sleep } from "../../../_helpers/sleep";
import { BaseDiscountModel } from "../discount-models/BaseDiscountModel";
import { PatchDiscountModel } from "../discount-models/PatchDiscountModel";
import { DiscountMethod } from "../discount-models/DiscountMethod";

@Component({
  selector: "edit-discount-form",
  templateUrl: "./edit-discount-form.component.html",
  styleUrls: ["./edit-discount-form.component.scss"],
})
export class EditDiscountFormComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public dataWasLoaded: boolean = false;

  @ViewChild("formTabSet") public formTabSet: NgbTabset;
  @Input("modalEventId") public modalUserId: "new" | string;
  @Input("discountToEdit") public discountToEdit: BaseDiscountModel;
  @Output("modalAction") public modalAction: EventEmitter<"close" | "save"> =
    new EventEmitter<"close" | "save">();

  public disableSave: boolean = false;
  public translations$: Observable<ITranslations>;
  @ViewChild("personalDetailsFormComponent")
  private personalDetailsFormComponent: UserManagementFormPersonalDetailsComponent;
  private _editDiscountFormState$: BehaviorSubject<EditDiscountFormState> =
    new BehaviorSubject<EditDiscountFormState | null>(null);

  public editDiscountFormState$: Observable<EditDiscountFormState> =
    this._editDiscountFormState$.asObservable();

  constructor(
    protected translationService: TranslationService,
    protected eventsService: EventsService,
    protected dataService: DataService,
    protected discountManagementService: DiscountManagementService,
    protected authenticationService: AuthenticationService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected modalService: NgbModal,
    protected changeDetectorRef: ChangeDetectorRef,
    protected sessionManagerService: SessionManagerService
  ) {
    this.translations$ = this.translationService.translations;
  }

  public ngOnInit(): void {
    this.initDiscountEditForm(this.modalUserId).then();
  }

  public async devAutoAction() {
    devToastMessage();
  }

  public ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.eventsService.resetAllEditDataData();
  }

  public async buildMegaFormObject() {
    return {};
  }

  public async submit() {
    let saveResponse;
    this.disableSave = true;
    const form = this._editDiscountFormState$.getValue();
    const finalPrice =
      this.discountManagementService.calculateFinalDiscountedPrice(
        form.childDiscountDescription.originalPrice,
        {
          value: form.amount.value,
          type: form.amount.type,
        }
      );

    const patchData: PatchDiscountModel = {
      final_subsidy_amount: finalPrice,
      number_of_payments: form.discountDetails.numberOfPayments,
      passed_subsidies_committee: form.discountDetails.passedCommittee,
      comments: form.discountDetails.comments,
    };

    saveResponse = await this.discountManagementService.submitNewDiscountForm(
      this.discountToEdit.discountId.toString(),
      patchData
    );

    const modalRef: NgbModalRef = this.modalService.open(GenericModalComponent);
    if (saveResponse.success) {
      modalRef.componentInstance.modalOptions = {
        approveOption: "אישור",
        cancelOption: "",
        modalHeader: "סיום",
        modalText: `הסבסוד עודכן בהצלחה!`,
        onlyApprove: true,
      };
      modalRef.result.then((res) => {
        this.closeParentEventModal();
      });
      return;
    } else {
      const isDisconnected =
        saveResponse.error && saveResponse.error.status === 401;

      const errorMessage =
        await EventValidationService.convertServer400ErrorsToReadableText(
          saveResponse.error
        );
      const modalText = isDisconnected
        ? "נותקת מהמערכת עקב חוסר פעילות. התחבר מחדש."
        : ` השמירה נכשלה. פרטים:   ${"\n" + errorMessage}`;
      modalRef.componentInstance.modalOptions = {
        approveOption: "אישור",
        cancelOption: "",
        modalHeader: "שגיאה",
        modalText,
        onlyApprove: true,
      };
      modalRef.result.then((res) => {
        if (isDisconnected) {
          this.closeParentEventModal();
          setTimeout(() => this.router.navigate(["/login"]), 1000);
        }
      });
      this.disableSave = false;
    }
  }

  public async closeParentEventModal() {
    this.modalAction.emit("close");
  }

  public updateDiscountEvent($event: EditDiscountFormState) {
    this._editDiscountFormState$.next($event);
    this.disableSave = this.hasValidationError();
  }

  protected async initDiscountEditForm(discountId: string) {
    /**
     * This is different from other components since we already have the discount data
     */
    try {
      const discountData =
        await this.discountManagementService.initSingleDiscountForm(
          this.discountToEdit
        );
      await sleep(500);

      this._editDiscountFormState$.next(discountData);
    } catch (err) {
      this.handleError(err);
    }

    this.dataWasLoaded = true;
    this.buildMegaForm();
  }

  protected buildMegaForm() {
    // this.userManagementMegaForm.addControl(
    //   "parentEventMain",
    //   this.userEditPersonalDetailsForm
    // );
  }

  protected handleError(err: any) {
    const isDisconnected = err.status === 401;
    const noUserPermission = err.status === 400;
    let modalText = err.toString();
    if (noUserPermission) {
      modalText = `אין לך הרשאה מספקת לערוך את הסבסוד  `;
    } else if (isDisconnected) {
      modalText = "נותקת מהמערכת עקב חוסר פעילות. התחבר מחדש.";
    }

    const modalRef: NgbModalRef = this.modalService.open(GenericModalComponent);
    modalRef.componentInstance.modalOptions = {
      approveOption: "אישור",
      cancelOption: "",
      modalHeader: "שגיאה",
      modalText,
      onlyApprove: true,
    };
    modalRef.result.then((res) => {
      this.closeParentEventModal().then((r) => r);
      if (isDisconnected) {
        setTimeout(() => this.router.navigate(["/login"]), 1000);
      }
    });
  }

  protected showValidationErrors(validationErrors: string[]) {
    const modalRef: NgbModalRef = this.modalService.open(GenericModalComponent);
    modalRef.componentInstance.modalOptions = {
      approveOption: "אישור",
      cancelOption: "",
      modalHeader: "שגיאה",
      modalText: `לשם שמירת הסבסוד : ${validationErrors.join(", ")}`,
      onlyApprove: true,
    };
    modalRef.result.then((res) => {});
  }

  protected validateMegaForm(): string[] {
    return [];
  }

  private hasValidationError() {
    const form = this._editDiscountFormState$.getValue();
    const amount = Number(form.amount.value) || 0;
    if (!amount || amount < 1 || amount > 100000) {
      return true;
    }
    if (form.amount.type === DiscountMethod.Number) {
      if (amount >= form.childDiscountDescription.originalPrice) {
        return true;
      }
    } else if (
      form.amount.type === DiscountMethod.Percentage &&
      amount >= 100
    ) {
      return true;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/discounts/edit-discount-form/edit-discount-form.component.ts